import { url2data } from "./fetch";

const feideLogout = async () => {
  return url2data(`core/auth/logout`, {});
};

const getFeideLoginUrl = async () => {
  return url2data(`core/feide/url`);
};

const verifyFeideCode = async (code) => {
  return url2data(`core/feide/code`, { code });
};

const refresh = async () => {
  return url2data(`core/auth/refresh`, {});
};

const getFeideGroups = async () => {
  return url2data(`core/feide/groups`);
};

const createOrganization = async (name) => {
  return url2data(`core/admin/groups`, { name, type: "organization" });
};

const listOrganizations = async (includeInactive = false) => {
  return url2data(`core/admin/groups?type=organization&includeInactive=${includeInactive}`);
};

const getMyPlans = async () => {
  return url2data(`core/plan`);
};

const getMyYearPlans = async () => {
  return url2data(`core/yearplan`);
};

const getMe = async () => {
  return url2data(`core/user/me`);
};

const loginWithPassword = async (email, password) => {
  return url2data(`core/auth/login/password`, { email, password });
};

const requestPasswordReset = async (email) => {
  return url2data(`core/auth/password-reset/request`, { email });
};

const resetPassword = async (token, password) => {
  return url2data(`core/auth/password-reset/confirm`, { token, password });
};

const setPassword = async (password) => {
  return url2data(`core/auth/password/set`, { password });
};

const signup = async (email, fullName, password) => {
  return url2data(`core/auth/signup`, { email, fullName, password });
};

const confirmWelcome = async (token, fullName, password) => {
  return url2data(`core/auth/welcome/confirm`, { token, fullName, password });
};

const getAuthConfig = async () => {
  return url2data(`core/auth/config`);
};

const getGroupById = async (id) => {
  return url2data(`core/admin/groups/${id}`);
};

const createGroup = async ({ name, type = "group", parentGroupId }) => {
  const payload = { name, type };
  if (parentGroupId) {
    payload.parentGroupId = parentGroupId;
  }
  return url2data(`core/admin/groups`, payload);
};

const updateGroup = async (id, data) => {
  return url2data(`core/admin/groups/${id}`, data, "PATCH");
};

const deleteGroup = async (id) => {
  return url2data(`core/admin/groups/${id}`, {}, "DELETE");
};

const getGroupMembers = async (groupId) => {
  return url2data(`core/admin/groups/${groupId}/members`);
};

const addGroupMember = async (groupId, userId, roleId, email) => {
  const body = { roleId };
  if (userId) {
    body.userId = userId;
  }
  if (email) {
    body.email = email;
  }
  return url2data(`core/admin/groups/${groupId}/members`, body);
};

const removeGroupMember = async (groupId, memberId) => {
  return url2data(`core/admin/groups/${groupId}/members/${memberId}`, {}, "DELETE");
};

const getUsersInOrganization = async (groupId) => {
  return url2data(`core/admin/groups/${groupId}/users`);
};

const createUser = async (groupId, email, fullName, roleId) => {
  return url2data(`core/admin/groups/${groupId}/users`, { email, fullName, roleId });
};

const getMySystemRoles = async () => {
  return url2data(`core/user/me/system-roles`);
};

const getMyGroupMemberships = async (includeInactive = false) => {
  return url2data(`core/user/me/group-memberships?includeInactive=${includeInactive}`);
};

const getGroupTree = async (groupId, includeHidden = false) => {
  return url2data(`core/admin/groups/${groupId}/tree?includeHidden=${includeHidden}`);
};

// FEIDE Groups Integration
const syncFeideGroups = async (organizationGroupId) => {
  return url2data(`core/feide-groups/sync`, { organizationGroupId });
};

const getFeideGroupsForOrganization = async (orgId, includeHidden = false) => {
  const url = `core/feide-groups/organization/${orgId}?includeHidden=${includeHidden}`;
  return url2data(url);
};

const getFeideGroupDetails = async (feideGroupId, orgId) => {
  return url2data(`core/feide-groups/${feideGroupId}/organization/${orgId}`);
};

const importFeideGroup = async (feideGroupId, orgId, parentGroupId, includeMembers = false) => {
  return url2data(`core/feide-groups/${feideGroupId}/organization/${orgId}/import`, {
    parentGroupId,
    includeMembers
  });
};

const mapFeideGroup = async (feideGroupId, orgId, digupGroupId) => {
  return url2data(`core/feide-groups/${feideGroupId}/organization/${orgId}/map`, {
    digupGroupId
  });
};

const unmapFeideGroup = async (feideGroupId, orgId) => {
  return url2data(`core/feide-groups/${feideGroupId}/organization/${orgId}/unmap`, {}, "DELETE");
};

const toggleHiddenFeideGroup = async (feideGroupId, orgId) => {
  return url2data(`core/feide-groups/${feideGroupId}/organization/${orgId}/toggle-hidden`, {}, "PATCH");
};

const toggleHiddenGroup = async (groupId) => {
  return url2data(`core/admin/groups/${groupId}/toggle-hidden`, {}, "PATCH");
};

// SUPERADMIN: Feide Organizations Management
const getAllFeideOrganizations = async () => {
  return url2data(`core/feide-groups/admin/organizations`);
};

const superadminImportOrganization = async (feideGroupId, includeMembers = false) => {
  return url2data(`core/feide-groups/admin/organizations/${feideGroupId}/import`, {
    includeMembers
  });
};

// SUPERADMIN: Feide Whitelist Management
const getFeideWhitelists = async (status = 'active', search = '') => {
  return url2data(`core/feide-whitelist?status=${status}&search=${search}`);
};

const getFeideWhitelist = async (id) => {
  return url2data(`core/feide-whitelist/${id}`);
};

const createFeideWhitelist = async (feideId, description, autoImportChildren = true, autoImportParents = false) => {
  return url2data(`core/feide-whitelist`, {
    feideId,
    description,
    autoImportChildren,
    autoImportParents
  });
};

const updateFeideWhitelist = async (id, updates) => {
  return url2data(`core/feide-whitelist/${id}`, updates, "PATCH");
};

const revokeFeideWhitelist = async (id, reason) => {
  return url2data(`core/feide-whitelist/${id}/revoke`, { reason });
};

const reactivateFeideWhitelist = async (id) => {
  return url2data(`core/feide-whitelist/${id}/reactivate`, {});
};

const processFeideWhitelist = async (id) => {
  return url2data(`core/feide-whitelist/${id}/process`, {});
};

const processAllFeideWhitelists = async () => {
  return url2data(`core/feide-whitelist/process-all`, {});
};

const previewFeideWhitelist = async (id) => {
  return url2data(`core/feide-whitelist/${id}/preview`);
};

const previewFeideId = async (feideId, autoImportChildren = true, autoImportParents = false) => {
  return url2data(`core/feide-whitelist/preview`, {
    feideId,
    autoImportChildren,
    autoImportParents
  });
};

const getAffectedGroupsByWhitelist = async (id) => {
  return url2data(`core/feide-whitelist/${id}/affected-groups`);
};

const previewRevokeWhitelist = async (id) => {
  return url2data(`core/feide-whitelist/${id}/revoke-preview`);
};

const getImportableOrganizations = async (id) => {
  return url2data(`core/feide-whitelist/${id}/importable-organizations`);
};

const importOrganizationsFromWhitelist = async (id) => {
  return url2data(`core/feide-whitelist/${id}/import-organizations`, {});
};

// eslint-disable-next-line import/no-anonymous-default-export
export default {
  feideLogout,
  getFeideLoginUrl,
  verifyFeideCode,
  refresh,
  getFeideGroups,
  createOrganization,
  listOrganizations,
  getMyPlans,
  getMyYearPlans,
  getMe,
  loginWithPassword,
  requestPasswordReset,
  resetPassword,
  setPassword,
  signup,
  confirmWelcome,
  getAuthConfig,
  getGroupById,
  createGroup,
  updateGroup,
  deleteGroup,
  getGroupMembers,
  addGroupMember,
  removeGroupMember,
  getUsersInOrganization,
  createUser,
  getMySystemRoles,
  getMyGroupMemberships,
  getGroupTree,
  // FEIDE Groups
  syncFeideGroups,
  getFeideGroupsForOrganization,
  getFeideGroupDetails,
  importFeideGroup,
  mapFeideGroup,
  unmapFeideGroup,
  toggleHiddenFeideGroup,
  toggleHiddenGroup,
  // Superadmin
  getAllFeideOrganizations,
  superadminImportOrganization,
  // Superadmin: Feide Whitelist
  getFeideWhitelists,
  getFeideWhitelist,
  createFeideWhitelist,
  updateFeideWhitelist,
  revokeFeideWhitelist,
  reactivateFeideWhitelist,
  processFeideWhitelist,
  processAllFeideWhitelists,
  previewFeideWhitelist,
  previewFeideId,
  getAffectedGroupsByWhitelist,
  previewRevokeWhitelist,
  getImportableOrganizations,
  importOrganizationsFromWhitelist,
};
